import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import DataTable from "react-data-table-component";
import LoaderComponent from "../../Common/LoaderComponent";
import { useSelector } from "react-redux";
import { reportSelector } from "../ReportSlice";
import { tableShortFormatDate } from "../../../Wrapper/CommonFun";
import { tableCustomStyles } from "../../../lib/utils";
import DataTablePaginationComponent from "../../Common/DataTablePaginationComponent";
import { filterDataByField } from "../../../lib/utility";
import ReportEmptyState from "../ReportEmptyState";

const formatSubmissionDate = (dateValue, isTotalRow, t) => {
  if (isTotalRow) {
    return t("submissionReport.table.total", { defaultValue: "Total" });
  }
  return tableShortFormatDate(dateValue, false);
};

const SubmissionTable = ({
  submissionTableData,
  isFetching,
  country,
  status,
  searchVal,
  pagination,
  handlePageChange,
  handlePerPageChange,
}) => {
  const { t } = useTranslation("summaryDelivery");
  const { isFetchingSubmissionUser } = useSelector(reportSelector);

  const columns = useMemo(() => {
    const cols = [
      {
        name: t("submissionReport.table.columns.date", { defaultValue: "Date" }),
        selector: (row) => formatSubmissionDate(row.Date, row.isTotalRow, t),
        sortFunction: (rowA, rowB) => {
          if (rowA.isTotalRow || rowB.isTotalRow) return 0;
          const dateA = new Date(rowA.Date);
          const dateB = new Date(rowB.Date);
          return dateA - dateB;
        },
        minWidth: "180px",
        sortable: true,
      },
      {
        name: t("submissionReport.table.columns.count", { defaultValue: "Count" }),
        selector: (row) => row.Count,
        sortable: true,
      },
    ];

    if (country && country !== "%") {
      cols.push({
        name: t("submissionReport.table.columns.country", {
          defaultValue: "Country",
        }),
        selector: (row) => row.Country,
        sortable: true,
      });
    }

    if (status && status !== "%") {
      cols.push({
        name: t("submissionReport.table.columns.status", {
          defaultValue: "Status",
        }),
        selector: (row) => row.Status,
        sortable: true,
      });
    }

    return cols;
  }, [country, status, t]);

  const data = useMemo(() => {
    if (!submissionTableData || !Array.isArray(submissionTableData)) {
      return [];
    }
    
    return submissionTableData.map((row) => {
      const dateValue = row.startDate || "";
      const isTotalRow = !dateValue || !moment(dateValue).isValid();

      return {
        Date: dateValue,
        isTotalRow,
        Count: row.totalSmsSend || 0,
        Country: row.country || "",
        Status: row.messageStatus || "",
      };
    });
  }, [submissionTableData]);

  const filteredData = useMemo(() => {
    if (!searchVal || searchVal.trim() === "") {
      return data;
    }
    return filterDataByField(data, "Date,Count,Country,Status", searchVal);
  }, [data, searchVal]);

  const paginatedData = useMemo(() => {
    const startIndex = pagination.current * pagination.size;
    const endIndex = startIndex + pagination.size;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, pagination.current, pagination.size]);

  return (
    
    <>
      <div className="table-responsive border br-12 table-action frozen m-3">
        {isFetchingSubmissionUser ? (
          <LoaderComponent />
        ) : (
          <DataTable
            className="h-100"
            columns={columns}
            data={paginatedData}
            highlightOnHover
            fixedHeader
            customStyles={tableCustomStyles()}
            noDataComponent={
              <ReportEmptyState title={t("noData", { ns: "common" })} />
            }
            pagination
            paginationServer
            paginationTotalRows={filteredData.length}
            paginationPerPage={pagination.size}
            paginationDefaultPage={pagination.current + 1}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerPageChange}
            paginationComponent={(props) => (
              <DataTablePaginationComponent
                {...props}
                rowsPerPage={pagination.size}
                rowCount={filteredData.length}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerPageChange}
                currentPage={pagination.current + 1}
                paginationRowsPerPageOptions={[
                  10, 30, 50, 100, 150, 200, 250, 350, 400, 450, 500,
                ]}
              />
            )}
          />
        )}
      </div>
    </>
  );
};

export default SubmissionTable;